import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fromJS, Map } from 'immutable'
import _get from 'lodash/get'
import { useLazyQuery } from '@apollo/client'
import { selectStaticTextByKey } from '../../services/static-text/selectors'
import { Tabs, Tab, TABS_JUSTIFY } from '../Tabs/index'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { selectAuth } from '../../services/auth/selectors'
import { GET_CONTENT } from '../../services/content/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
} from '../../services/apollo-graphql/index'
import {
  CURRENT_LESSON_TAB_VALUE,
  DETAIL_PAGE_STATIC_KEY,
  EPISODE_TAB_VALUE,
  GUIDE_OVERVIEW_TAB_VALUE,
  RELATED_TAB_VALUE,
  SERIES_TAB_VALUE,
  VIDEO_TAB_VALUE,
} from '../../services/detail/index'
import {
  createTitleGroupId,
  getFormattedContentData,
  isVideo,
  isGuide,
  isSeries,
  CONTENT_IMAGE_LIST,
} from '../../services/content/index'
import { selectResolver } from '../../services/resolver/selectors'
import { setCommentsVisible } from '../../services/comments/actions'
import { selectIsCommentsVisible } from '../../services/comments/selectors'
import { selectAppViewportWidth, selectAppViewportHeight } from '../../services/app/selectors'
import { get as getConfig } from '../../config/index'
import { addNewRelicAction } from '../../services/app/_newrelic'
import DetailPageLayoutTemplate from './DetailPageLayoutTemplate'
import DetailJumbotron from './DetailJumbotron'
import DetailPlaceholder from './DetailPlaceholder'
import DetailSeries from './DetailSeries'
import DetailVideoEpisode from './DetailVideoEpisode'
import DetailRelated from './DetailRelated'
import DetailMarketplaceProduct from './DetailMarketplaceProduct'
import DetailSeriesSeasons from './DetailSeriesSeasons'
import DetailRelatedCourses from './DetailRelatedCourses'
import DetailVideoPlayer from './DetailVideoPlayer'

const config = getConfig()
const videoDetailInlinePlayerEnabled = !!_get(config, ['features', 'videoDetailInlinePlayer'])

const getTabsGraphQL = (props) => {
  const { tabData } = props
  const contentType = tabData.get('contentType')
  const contentSubtype = tabData.get('contentSubtype')
  const displayType = tabData.get('displayType')
  const isVideoPage = isVideo({ contentType, data: tabData })
  const isSeriesPage = isSeries({ contentType })
  const hasFeaturedEpisode = Map.isMap(tabData) && Map.isMap(tabData.get('featuredContent')) && tabData.get('featuredContent').size
  const isGuideDetailPage = isGuide({ contentSubtype, displayType })
  const staticText = useSelector(selectStaticTextByKey(DETAIL_PAGE_STATIC_KEY))

  if (isGuideDetailPage) {
    return [
      {
        label: staticText.get('guideOverviewTabTitle', ''),
        value: GUIDE_OVERVIEW_TAB_VALUE,
        TabComponent: DetailSeries,
      },
      {
        label: staticText.get('currentLessonTabTitle', ''),
        value: CURRENT_LESSON_TAB_VALUE,
        TabComponent: DetailVideoEpisode,
      },
      {
        label: staticText.get('relatedTabTitle', ''),
        value: RELATED_TAB_VALUE,
        TabComponent: DetailRelated,
      },
    ]
  }

  if (isVideoPage) {
    return [
      {
        label: staticText.get('videoInfoTabTitle', ''),
        value: VIDEO_TAB_VALUE,
        TabComponent: DetailVideoEpisode,
      },
      {
        label: staticText.get('relatedTabTitle', ''),
        value: RELATED_TAB_VALUE,
        TabComponent: DetailRelated,
      },
    ]
  }

  if (isSeriesPage && !hasFeaturedEpisode) {
    return [
      {
        label: staticText.get('seriesInfoTabTitle', ''),
        value: SERIES_TAB_VALUE,
        TabComponent: DetailSeries,
      },
    ]
  }

  return [
    {
      label: staticText.get('episodeInfoTabTitle', ''),
      value: EPISODE_TAB_VALUE,
      TabComponent: DetailVideoEpisode,
    },
    {
      label: staticText.get('seriesInfoTabTitle', ''),
      value: SERIES_TAB_VALUE,
      TabComponent: DetailSeries,
    },
    {
      label: staticText.get('relatedTabTitle', ''),
      value: RELATED_TAB_VALUE,
      TabComponent: DetailRelated,
    },
  ]
}

function DetailPageV2 (props) {
  const { history, location, apolloClient } = props
  const isCommentsVisible = useSelector(selectIsCommentsVisible)
  const userLanguage = useSelector(selectPrimaryLanguage)
  const auth = useSelector(selectAuth)
  const authJwt = auth.get('jwt')
  const resolver = useSelector(selectResolver)
  const dispatch = useDispatch()
  const [getDetailData, { data, loading }] = useLazyQuery(GET_CONTENT)
  const detailData = fromJS(_get(data, ['contentList', 'content', 0], []) || [])
  const formattedDetailData = fromJS(getFormattedContentData(detailData)) || Map()
  const language = [userLanguage, '_primary']
  const resolverPath = resolver.getIn(['data', 'path'])
  const locationPath = _get(location, 'pathname')
  const contentId = resolver.getIn(['data', 'id'])
  const contentType = resolver.getIn(['data', 'content_type'])
  const [detailId, setDetailId] = useState(contentId)
  const [detailAuth, setDetailAuth] = useState(auth || Map())
  const detailIsSynced = contentId === detailId && locationPath === resolverPath
  const viewportWidth = useSelector(selectAppViewportWidth)
  const viewportHeight = useSelector(selectAppViewportHeight)

  // Inline playback state (mobile portrait)
  // Used to display Oracle Guide On Side
  const isInlinePlaybackActive = videoDetailInlinePlayerEnabled
    && viewportWidth < 819 && viewportHeight > viewportWidth
    && !!_get(location, ['query', 'fullplayer'])

  const video = useSelector((state) => state.video)
  const media = useSelector((state) => state.media)
  const userVideo = useSelector((state) => state.userVideo)

  const tabData = formattedDetailData
  const dataContentType = tabData.get('contentType')
  const contentSubtype = tabData.get('contentSubtype')
  const displayType = tabData.get('displayType')

  const isGuideDetailPage = isGuide({ contentSubtype, displayType })
  const isVideoDetailPage = isVideo({ contentType: dataContentType, data: tabData })

  const titleGroupId = detailId && contentType ? createTitleGroupId(detailId, contentType) : null
  const detailAuthUid = detailAuth.get('uid')
  const authUid = auth.get('uid')

  useEffect(() => {
    if (titleGroupId && detailIsSynced) {
      getDetailData({
        variables: {
          language,
          primaryLanguage: [userLanguage],
          titleGroupIds: [titleGroupId],
          imageType: CONTENT_IMAGE_LIST,
          limitedData: false,
          includeSeriesImage: true,
          learnerLevelLanguage: userLanguage,
        },
        fetchPolicy: detailAuthUid !== authUid
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt: authJwt,
        },
      }).catch((e) => {
        const networkError = e && e.networkError
        const httpStatus = networkError && networkError.statusCode
        addNewRelicAction('GetContentQueryFailed', {
          titleGroupId,
          errorMessage: e instanceof Error ? e.message : String(e),
          ...(httpStatus != null ? { httpStatus } : {}),
        })
      })
    }
  }, [detailIsSynced, userLanguage, titleGroupId, authJwt, detailAuthUid, authUid])

  useEffect(() => {
    if (contentId) {
      setDetailId(contentId)
    }
  }, [contentId])

  // set the user's auth for this page to help handle loading detail data
  // if the user's auth changes (renewal, login, etc.), then load detail data again
  useEffect(() => {
    if (detailAuthUid !== authUid) {
      setDetailAuth(auth)
    }
  }, [auth, detailAuthUid, authUid])

  const tabs = getTabsGraphQL({ tabData: formattedDetailData })
  let currentTabIndex = 0
  const detailContentType = formattedDetailData.get('contentType')
  const formattedContentSubtype = formattedDetailData.get('contentSubtype')
  const formattedDisplayType = formattedDetailData.get('displayType')
  const isGuidePage = isGuide({
    contentSubtype: formattedContentSubtype,
    displayType: formattedDisplayType,
  })
  const isSeriesPage = isSeries({ contentType: detailContentType })
  const detailDataHasFeaturedEpisode = isSeriesPage
    && Map.isMap(formattedDetailData)
    && Map.isMap(formattedDetailData.get('featuredContent'))
    && formattedDetailData.get('featuredContent').size

  // select series tab only if there is no featured episode
  if (isSeriesPage && !detailDataHasFeaturedEpisode) {
    currentTabIndex = 1
  }

  if (isGuidePage) {
    currentTabIndex = isSeriesPage ? 0 : 1
  }

  // it's for action buttons, not for Tabs component
  const [selectedTab, setSelectedTab] = useState(tabs[currentTabIndex]?.value)

  const handleChange = (index) => {
    setSelectedTab(tabs[index]?.value)

    if (isCommentsVisible) {
      dispatch(setCommentsVisible(false))
    }
  }

  const shouldShowPlayer = videoDetailInlinePlayerEnabled && !!_get(location, ['query', 'fullplayer'])

  const renderRowOne = () => {
    if (shouldShowPlayer) {
      return (
        <DetailVideoPlayer
          detailData={formattedDetailData}
          location={location}
          history={history}
          apolloClient={apolloClient}
          video={video}
          media={media}
          userVideo={userVideo}
        />
      )
    }

    return (
      <DetailJumbotron detailData={formattedDetailData} location={location} />
    )
  }

  if (!data && loading) {
    return (
      <DetailPlaceholder />
    )
  }

  return (
    <div className="detail-page">
      <DetailPageLayoutTemplate
        rowOne={renderRowOne()}
        rowTwo={
          <Tabs
            justify={TABS_JUSTIFY.LEFT}
            activeTab={currentTabIndex}
            onChange={handleChange}
          >
            {tabs.map((tab) => {
              const { value, label, TabComponent } = tab

              return (
                <Tab key={value} label={label}>
                  {
                    <TabComponent
                      selectedTab={selectedTab}
                      tabData={formattedDetailData || Map()}
                      isInlinePlaybackActive={isInlinePlaybackActive}
                    />
                  }
                </Tab>
              )
            })}
          </Tabs>
        }
        rowThree={
          !isGuideDetailPage
          && selectedTab !== RELATED_TAB_VALUE
          && <>
            <DetailRelatedCourses
              tabData={formattedDetailData || Map()}
            />
            <DetailMarketplaceProduct
              detailData={formattedDetailData || Map()}
            />
          </>
        }
        rowFour={
          !isVideoDetailPage
            && <DetailSeriesSeasons
                history={history}
                detailData={formattedDetailData || Map()}
                />
        }
      />
    </div>
  )
}

export default DetailPageV2
